import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil, Trash2, ShieldCheck, KeyRound } from "lucide-react";
import { toast } from "sonner";

const PERM_LABELS = {
  can_create_sales: "Registrar ventas",
  can_view_history: "Ver historial",
  can_manage_car_types: "Gestionar tipos de auto",
  can_manage_extras: "Gestionar extras",
  can_manage_expenses: "Gestionar gastos",
  can_view_reports: "Ver reportes",
  can_manage_users: "Gestionar usuarios",
};

const ROLE_LABELS = {
  admin: "Administrador",
  gerente: "Gerente",
  caja: "Caja",
};

const ROLE_BADGE = {
  admin: "bg-blue-100 text-blue-800 border-blue-200",
  gerente: "bg-emerald-100 text-emerald-800 border-emerald-200",
  caja: "bg-amber-100 text-amber-800 border-amber-200",
};

const EMPTY = {
  email: "",
  password: "",
  name: "",
  role: "caja",
  permissions: null,
};

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [meta, setMeta] = useState({ permissions: [], roles: [], defaults: {} });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [perms, setPerms] = useState({});

  const load = useCallback(async () => {
    const [u, m] = await Promise.all([
      api.get("/users"),
      api.get("/users/meta"),
    ]);
    setUsers(u.data);
    setMeta(m.data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setPerms(meta.defaults?.caja || {});
    setOpen(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({
      email: u.email,
      password: "",
      name: u.name || "",
      role: u.role,
      permissions: u.permissions,
    });
    setPerms({ ...u.permissions });
    setOpen(true);
  };

  const onRoleChange = (role) => {
    setForm((f) => ({ ...f, role }));
    setPerms({ ...(meta.defaults?.[role] || {}) });
  };

  const togglePerm = (key) =>
    setPerms((p) => ({ ...p, [key]: !p[key] }));

  const onSave = async () => {
    try {
      if (editing) {
        const payload = {
          name: form.name,
          role: form.role,
          permissions: perms,
        };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editing.id}`, payload);
        toast.success("Usuario actualizado");
      } else {
        if (!form.email || !form.password) {
          toast.error("Email y contraseña son obligatorios");
          return;
        }
        await api.post("/users", {
          email: form.email,
          password: form.password,
          name: form.name,
          role: form.role,
          permissions: perms,
        });
        toast.success("Usuario creado");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const onDelete = async (id) => {
    try {
      await api.delete(`/users/${id}`);
      toast.success("Usuario eliminado");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="space-y-8" data-testid="users-page">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
            Usuarios y permisos
          </h1>
          <p className="text-slate-500 mt-1">
            Crea usuarios con rol (admin, gerente, caja) y ajusta permisos uno a uno.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              onClick={openNew}
              className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
              data-testid="user-add-button"
            >
              <Plus className="w-4 h-4" /> Nuevo usuario
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {editing ? `Editar ${editing.email}` : "Crear nuevo usuario"}
              </DialogTitle>
              <DialogDescription>
                Define el rol y ajusta cada permiso de forma individual.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="mb-1.5 block">Email</Label>
                  <Input
                    type="email"
                    value={form.email}
                    disabled={!!editing}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    data-testid="user-email-input"
                  />
                </div>
                <div>
                  <Label className="mb-1.5 block">Nombre</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="user-name-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="mb-1.5 block">Rol</Label>
                  <Select value={form.role} onValueChange={onRoleChange}>
                    <SelectTrigger data-testid="user-role-trigger">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(meta.roles || ["admin", "gerente", "caja"]).map((r) => (
                        <SelectItem key={r} value={r}>
                          {ROLE_LABELS[r] || r}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="mb-1.5 block">
                    {editing ? "Nueva contraseña (opcional)" : "Contraseña"}
                  </Label>
                  <Input
                    type="password"
                    value={form.password}
                    placeholder={editing ? "Dejar vacío para mantener" : ""}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    data-testid="user-password-input"
                  />
                </div>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <ShieldCheck className="w-4 h-4 text-blue-600" />
                  <span className="font-display font-semibold text-sm">
                    Permisos individuales
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                  {(meta.permissions || Object.keys(PERM_LABELS)).map((k) => {
                    const adminLock = form.role === "admin";
                    return (
                      <label
                        key={k}
                        className={`flex items-center justify-between gap-3 px-3 py-2 rounded-md border bg-white ${
                          adminLock ? "border-blue-100" : "border-slate-200"
                        }`}
                      >
                        <span className="text-sm">{PERM_LABELS[k] || k}</span>
                        <Switch
                          checked={adminLock ? true : !!perms[k]}
                          disabled={adminLock}
                          onCheckedChange={() => togglePerm(k)}
                          data-testid={`perm-${k}`}
                        />
                      </label>
                    );
                  })}
                </div>
                {form.role === "admin" && (
                  <p className="text-xs text-slate-500 mt-2">
                    Un administrador siempre conserva todos los permisos.
                  </p>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>
                Cancelar
              </Button>
              <Button
                onClick={onSave}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="user-save-button"
              >
                Guardar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </header>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
              <th className="py-3 px-4">Usuario</th>
              <th className="py-3 px-4">Rol</th>
              <th className="py-3 px-4">Permisos activos</th>
              <th className="py-3 px-4 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const activePerms = Object.entries(u.permissions || {})
                .filter(([, v]) => v)
                .map(([k]) => PERM_LABELS[k] || k);
              return (
                <tr
                  key={u.id}
                  className="border-t border-slate-100"
                  data-testid={`user-row-${u.id}`}
                >
                  <td className="py-3 px-4">
                    <div className="font-medium">{u.name || u.email}</div>
                    <div className="text-xs text-slate-500">{u.email}</div>
                  </td>
                  <td className="py-3 px-4">
                    <Badge variant="outline" className={ROLE_BADGE[u.role] || ""}>
                      {ROLE_LABELS[u.role] || u.role}
                    </Badge>
                  </td>
                  <td className="py-3 px-4 text-slate-600">
                    {(() => {
                      if (u.role === "admin") return "Todos";
                      if (activePerms.length) return activePerms.join(", ");
                      return "—";
                    })()}
                  </td>
                  <td className="py-3 px-4 text-right">
                    <div className="inline-flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEdit(u)}
                        data-testid={`user-edit-${u.id}`}
                        title="Editar"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      {u.id !== me?.id && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-red-600"
                              data-testid={`user-delete-${u.id}`}
                              title="Eliminar"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>¿Eliminar {u.email}?</AlertDialogTitle>
                              <AlertDialogDescription>
                                El usuario perderá acceso de inmediato.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => onDelete(u.id)}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                Eliminar
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="text-xs text-slate-500 flex items-center gap-2">
        <KeyRound className="w-3.5 h-3.5" />
        Los cambios surten efecto en el próximo inicio de sesión del usuario.
      </div>
    </div>
  );
}
